import { ReactComponent as EqualLineIcon } from 'assets/icons/EqualLineIcon.svg';
import {
  Grid,
  Box,
  Typography,
  Tooltip,
  Switch,
  Divider,
  IconButton,
  Button,
  Popover,
} from '@mui/material';
import { ConfigEntryType, ConfigType } from 'config/config';
import useUserData from 'hooks/useUserData';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { catchErrorMessage } from 'utility/helpers';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTheme } from '@mui/material/styles';
import { useConfigQuery, ConfigAPI, CONFIGS } from '@buildwise/libs/configurations';
import DeleteBinLineIcon from 'remixicon-react/DeleteBinLineIcon';
import CompanyAPI from 'api/CompanyAPI';
import { ChromePicker } from 'react-color';

type RenderAdminConfigEntryItemProps = {
  config: ConfigType;
  entry: ConfigEntryType;
  index: number;
};

function RenderAdminConfigEntryItem({ config, entry, index }: RenderAdminConfigEntryItemProps) {
  const theme = useTheme();
  const { isNovaAdmin } = useUserData();
  const [openTooltip, setOpenTooltip] = useState('');
  const { updateConfigCache, configs } = useConfigQuery();
  const { attributes, listeners, transform, transition, setNodeRef, isDragging } = useSortable({
    id: entry?.id || '',
    disabled: !isNovaAdmin,
  });

  const [colorAnchorEl, setColorAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedColor, setSelectedColor] = useState<string>(
    entry.referenceObject?.color || 'transparent',
  );

  const handleColorButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setColorAnchorEl(event.currentTarget);
  };

  const handleColorChange = (color: any) => {
    if (typeof color.rgb?.a === 'number') {
      const alphaHex = Math.round(color.rgb.a * 255)
        .toString(16)
        .padStart(2, '0');
      setSelectedColor(`${color.hex}${alphaHex}`);
    } else {
      setSelectedColor(color.hex);
    }
  };

  const handleColorClose = async () => {
    setColorAnchorEl(null);
    if (selectedColor !== entry.referenceObject?.color) {
      await updateConfig({
        config,
        updatedConfigEntry: {
          ...entry,
          referenceObject: {
            ...entry.referenceObject,
            color: selectedColor,
          },
        },
      });
    }
  };

  const updateConfig = async ({
    config,
    updatedConfigEntry,
  }: {
    config: ConfigType;
    updatedConfigEntry: ConfigEntryType;
  }) => {
    const updatedConfigEntries = [...config.entries];
    const index = updatedConfigEntries.findIndex((item) => item.id === updatedConfigEntry.id);

    if (index > -1) {
      updatedConfigEntries[index] = {
        ...updatedConfigEntries[index],
        ...updatedConfigEntry,
      };
    }

    try {
      const { data } = await ConfigAPI.updateConfig({
        ...config,
        entries: updatedConfigEntries,
      });

      setOpenTooltip(updatedConfigEntry.entryCode);
      setTimeout(() => {
        setOpenTooltip('');
      }, 2000);

      updateConfigCache({
        updatedConfig: {
          configs: configs?.map((config) =>
            config?.configName === data?.config?.configName ? data?.config : config,
          ),
        },
      });
    } catch (e) {
      toast.error(catchErrorMessage(e));
    }
  };

  const deleteConfig = async (configArray: ConfigEntryType[], index: number) => {
    try {
      configArray[index].deleteFlag = true;
      const { data } = await ConfigAPI.updateConfig({
        ...config,
        entries: configArray,
      });
      toast.success(`${configArray[index].entryCode} deleted successfully`);
      updateConfigCache({
        updatedConfig: {
          configs: configs?.map((config) =>
            config?.configName === data?.config?.configName ? data?.config : config,
          ),
        },
      });
    } catch (e) {
      toast.error(catchErrorMessage(e));
    }
  };

  return (
    <Grid
      item
      md={12}
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        py={isNovaAdmin ? 0.5 : 1}
        sx={{
          cursor: 'pointer',
          ...(isDragging && {
            boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
          }),
          '&:hover': {
            '& .sort-icon': {
              visibility: 'visible',
            },
          },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          flex={1}
          ml={isNovaAdmin ? -4 : 0}
          {...attributes}
          {...listeners}
        >
          {isNovaAdmin && (
            <IconButton size="small" className="sort-icon" sx={{ visibility: 'hidden' }}>
              <EqualLineIcon width={24} color={theme.palette.custom.coolGrey} />
            </IconButton>
          )}
          <Typography ml={0.5}>
            {entry?.entryCode} {entry.entryDescription && `- ${entry.entryDescription}`}
          </Typography>
        </Box>
        {(config.configName === CONFIGS.SUGGESTION_TRACKER_TASKS_STATUS.configName ||
          config.configName === CONFIGS.BUILDWISE_PRODUCT_TRACKER_TASKS_STATUS.configName) && (
          <Box mr={2}>
            <Button
              variant="outlined"
              size="small"
              sx={{
                minWidth: 40,
                width: 50,
                height: 25,
                borderRadius: 1,
                p: 0.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderColor: theme.palette.custom.blueChalk,
                '&:hover': {
                  borderColor: theme.palette.custom.line,
                },
              }}
              onClick={handleColorButtonClick}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: 0.5,
                  background: selectedColor,
                  transition: 'background 0.2s',
                }}
              />
            </Button>
            <Popover
              open={Boolean(colorAnchorEl)}
              anchorEl={colorAnchorEl}
              onClose={handleColorClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              slotProps={{
                paper: {
                  sx: {
                    marginTop: 0.5,
                    boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
                    borderRadius: 2,
                    '& .chrome-picker': {
                      boxShadow: 'none !important',
                      border: 'none !important',
                    },
                  },
                },
              }}
            >
              <ChromePicker
                color={selectedColor}
                onChange={handleColorChange}
                styles={{
                  default: {
                    picker: {
                      width: 250,
                      boxShadow: 'none',
                      border: 'none',
                    },
                  },
                }}
              />
            </Popover>
          </Box>
        )}
        {isNovaAdmin && (
          <Box>
            <Tooltip
              open={openTooltip === entry.entryCode}
              placement="top"
              title={
                <Typography>
                  {entry?.entryCode} is {entry.isActive ? 'activated' : 'deactivated'}
                </Typography>
              }
            >
              <Switch
                disableRipple
                sx={{ m: 1 }}
                checked={entry.isActive}
                onChange={(_, checked) =>
                  updateConfig({
                    config,
                    updatedConfigEntry: {
                      ...entry,
                      isActive: checked,
                    },
                  })
                }
              />
            </Tooltip>
            {isNovaAdmin && (
              <IconButton onClick={() => deleteConfig(config.entries, index)}>
                <DeleteBinLineIcon size={20} color={theme.palette.custom.redWine} />
              </IconButton>
            )}
          </Box>
        )}
      </Box>
      {config.entries.length - 1 !== index && !isDragging && <Divider />}
    </Grid>
  );
}

export default RenderAdminConfigEntryItem;
